/**
 * Credit Control - Dataverse Hooks
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { IOrderQueryParams } from "@/features/orders-management/types";
import { getCreditControlOrdersFromDataverse, getCreditControlOrdersCountApi, approveCreditLimit, IApproveCreditLimitRequest } from "../api";
import { queryKey } from "../constants";

/**
 * Hook to fetch credit control orders from Dataverse
 * Automatically filters by statuscode = 736000002 (pending credit approval)
 */
export const useCreditControlOrders = (params: {
  queryParams: IOrderQueryParams;
  enabled: boolean;
}) => {
  return useQuery({
    queryKey: [queryKey.getCreditControlOrders, params.queryParams],
    queryFn: () => getCreditControlOrdersFromDataverse(params.queryParams),
    enabled: params.enabled,
  });
};

/**
 * Hook to fetch the count of orders pending credit approval — used for the
 * sidebar badge, cheap aggregate query instead of fetching the full list
 */
export const useCreditControlOrdersCount = (enabled: boolean = true, region?: number | null) => {
  return useQuery({
    queryKey: [queryKey.getCreditControlOrdersCount, region],
    queryFn: () => getCreditControlOrdersCountApi(region),
    enabled,
    staleTime: 60 * 1000,
    select: (data) => data?.value?.[0]?.count ?? 0,
  });
};

/**
 * Hook to approve or reject credit limit for an order
 * Automatically invalidates credit control orders query on success
 */
export const useApproveCreditLimit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [queryKey.approveCreditLimit],
    mutationFn: (data: IApproveCreditLimitRequest) => approveCreditLimit(data),
    onSuccess: () => {
      // Invalidate and refetch credit control orders after approval/rejection
      queryClient.invalidateQueries({ queryKey: [queryKey.getCreditControlOrders] });
      queryClient.invalidateQueries({ queryKey: [queryKey.getCreditControlOrdersCount] });
    },
  });
};
